import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Stethoscope, HardHat, Pencil, X, Plus, User, Briefcase, Calendar as CalendarIcon, FileSpreadsheet, Loader2, UserMinus, UserPlus } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useFetch, Spinner } from '../lib/useFetch';
import { workersApi, companiesApi, formazioneApi, visiteApi, dpiApi, reportsApi, rolesApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { workerFields, formazioneFields, visitaFields, dpiFields } from '../lib/entitySchemas';
import { statusBadge } from './FormazionePage';
import { fmtDate } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { DismissWorkerDialog, RehireWorkerDialog } from '../components/DismissalDialogs';

const WorkerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [downloading, setDownloading] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [rehireOpen, setRehireOpen] = useState(false);

  const { data: worker, loading, reload: reloadWorker } = useFetch(() => workersApi.get(id), [id]);
  const { data: companies } = useFetch(() => companiesApi.list({ status: 'all' }), []);
  const { data: rolesCatalog } = useFetch(() => (isClient ? Promise.resolve({ data: [] }) : rolesApi.catalog()), [isClient]);
  const { data: formazione, reload: reloadF } = useFetch(() => formazioneApi.list({ workerId: id }), [id]);
  const { data: visite, reload: reloadV } = useFetch(() => visiteApi.list({ workerId: id }), [id]);
  const { data: dpi, reload: reloadD } = useFetch(() => dpiApi.list({ workerId: id }), [id]);

  const roleLabelOf = React.useCallback((key) => {
    const e = (rolesCatalog || []).find((x) => x.roleKey === key);
    return e ? e.roleLabel : key;
  }, [rolesCatalog]);

  const ruoliRendered = useMemo(() => {
    const r = worker?.ruoli;
    if (!r) return null;
    if (Array.isArray(r)) return r.length ? r : null;
    // legacy string: split on separators
    const parts = String(r).split(/[;,|/]+/).map((s) => s.trim()).filter(Boolean);
    return parts.length ? parts : null;
  }, [worker]);

  const workerEdit = useCrudActions({ api: workersApi, label: 'lavoratore', onChange: reloadWorker });
  const fCrud = useCrudActions({ api: formazioneApi, label: 'corso', onChange: reloadF });
  const vCrud = useCrudActions({ api: visiteApi, label: 'visita', onChange: reloadV });
  const dCrud = useCrudActions({ api: dpiApi, label: 'DPI', onChange: reloadD });

  const company = useMemo(() => (companies || []).find((c) => c.id === worker?.companyId), [companies, worker]);
  const isDismesso = worker?.status === 'dismesso';

  if (loading || !worker) return <Spinner />;

  const defaultsFor = (extra = {}) => ({ workerId: worker.id, companyId: worker.companyId, stato: 'valido', ...extra });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 font-semibold text-slate-700">
          <ArrowLeft size={15} /> Indietro
        </button>
        <Link to="/lavoratori" className="text-slate-500 hover:text-[#023F80]">Lavoratori</Link>
        <span className="text-slate-300">/</span>
        <span className="font-semibold text-slate-800 truncate">{worker.firstName} {worker.lastName}</span>
      </div>

      {/* Header card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#023F80] to-[#023F80] text-white p-6 sm:p-8">
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-[#FFCB05]/15" />
        <div className="relative flex items-start gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 text-2xl font-extrabold text-[#FFCB05]">
            {(worker.firstName || ' ').charAt(0)}{(worker.lastName || ' ').charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight">{worker.firstName} {worker.lastName}</h1>
            <p className="text-white/70 mt-2 text-sm font-mono">{worker.fiscalCode || '— nessun CF —'}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Tag icon={Briefcase}>{worker.mansione || 'Mansione non definita'}</Tag>
              {company && <Tag icon={User}>{company.name}</Tag>}
              <Tag icon={CalendarIcon}>Assunzione: {fmtDate(worker.dataAssunzione)}</Tag>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${isDismesso ? 'bg-slate-400/20 text-slate-200 border border-slate-300/30' : 'bg-emerald-400/20 text-emerald-200 border border-emerald-300/30'}`}>
                {isDismesso ? `Dismesso · ${fmtDate(worker.dismessoAt) || '—'}` : 'Attivo'}
              </span>
            </div>
            {isDismesso && worker.dismissalReason && (
              <p className="mt-3 text-xs text-white/70 italic">Motivo: {worker.dismissalReason}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {!isClient && !isDismesso && <button onClick={() => workerEdit.onEdit(worker)} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold flex items-center gap-2"><Pencil size={13} /> Modifica</button>}
            <button
              onClick={async () => {
                setDownloading(true);
                try { await reportsApi.workerExcel(worker.id); toast.success('Download avviato'); }
                catch (e) { toast.error(e.response?.data?.detail || 'Errore download'); }
                finally { setDownloading(false); }
              }}
              data-testid="worker-export-excel"
              disabled={downloading}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold flex items-center gap-2 disabled:opacity-50"
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} className="text-emerald-300" />} Excel scadenze
            </button>
            {!isClient && !isDismesso && (
              <button onClick={() => setDismissOpen(true)} data-testid="worker-dismiss-btn" className="px-4 py-2 rounded-lg bg-red-500/90 hover:bg-red-600 text-white text-xs font-bold flex items-center gap-2">
                <UserMinus size={13} /> Dismetti
              </button>
            )}
            {!isClient && isDismesso && (
              <button onClick={() => setRehireOpen(true)} data-testid="worker-rehire-btn" className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-2">
                <UserPlus size={13} /> Riassumi
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Anagrafica */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="font-bold text-gray-800 mb-4 text-sm">Dati anagrafici</h3>
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <Row label="Data di nascita">{fmtDate(worker.birthDate)}</Row>
          <Row label="Codice fiscale">{worker.fiscalCode || '—'}</Row>
          <Row label="Mansione">{worker.mansione || '—'}</Row>
          <Row label="Sede di lavoro">{worker.sede || '—'}</Row>
          <Row label="Contratto">{worker.contratto || '—'}</Row>
          <Row label="Data assunzione">{fmtDate(worker.dataAssunzione)}</Row>
          <Row label="Ruoli">
            {ruoliRendered ? (
              <div className="flex flex-wrap gap-1">
                {ruoliRendered.map((k) => (
                  <span
                    key={k}
                    title={k}
                    data-testid={`worker-detail-ruolo-${k}`}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-100"
                  >
                    {roleLabelOf(k)}
                  </span>
                ))}
              </div>
            ) : '—'}
          </Row>
          <Row label="Stato">{worker.stato || '—'}</Row>
          <Row label="Azienda">{company?.name || '—'}</Row>
        </dl>
      </div>

      {/* Tabs of related data */}
      <Tabs defaultValue="formazione" className="w-full">
        <TabsList className="bg-slate-100 p-1 mb-4 flex-wrap h-auto">
          <TabTrigger value="formazione" icon={GraduationCap}>Formazione ({formazione?.length || 0})</TabTrigger>
          <TabTrigger value="visite" icon={Stethoscope}>Visite ({visite?.length || 0})</TabTrigger>
          <TabTrigger value="dpi" icon={HardHat}>DPI ({dpi?.length || 0})</TabTrigger>
        </TabsList>

        <TabsContent value="formazione">
          <SubSection title="Corsi di formazione" onAdd={() => fCrud.onCreate(defaultsFor())}>
            <RelatedTable
              rows={formazione}
              cols={[
                { key: 'corso', label: 'Corso' },
                { key: 'dataRilascio', label: 'Rilascio', fmt: fmtDate },
                { key: 'scadenza', label: 'Scadenza', fmt: fmtDate },
                { key: 'durata', label: 'Ore', render: (r) => `${r.durata || 0}h` },
                { key: 'stato', label: 'Stato', render: (r) => statusBadge(r.stato) },
              ]}
              onEdit={fCrud.onEdit}
              onDelete={fCrud.onDelete}
            />
          </SubSection>
        </TabsContent>

        <TabsContent value="visite">
          <SubSection title="Visite mediche" onAdd={() => vCrud.onCreate(defaultsFor())}>
            <RelatedTable
              rows={visite}
              cols={[
                { key: 'tipo', label: 'Tipo' },
                { key: 'dataVisita', label: 'Data visita', fmt: fmtDate },
                { key: 'scadenza', label: 'Scadenza', fmt: fmtDate },
                { key: 'medico', label: 'Medico' },
                { key: 'idoneita', label: 'Idoneità' },
                { key: 'stato', label: 'Stato', render: (r) => statusBadge(r.stato) },
              ]}
              onEdit={vCrud.onEdit}
              onDelete={vCrud.onDelete}
            />
          </SubSection>
        </TabsContent>

        <TabsContent value="dpi">
          <SubSection title="DPI assegnati" onAdd={() => dCrud.onCreate(defaultsFor({ stato: 'consegnato' }))}>
            <RelatedTable
              rows={dpi}
              cols={[
                { key: 'tipo', label: 'DPI' },
                { key: 'dataConsegna', label: 'Consegna', fmt: fmtDate },
                { key: 'scadenza', label: 'Scadenza', fmt: fmtDate },
                { key: 'taglia', label: 'Taglia' },
              ]}
              onEdit={dCrud.onEdit}
              onDelete={dCrud.onDelete}
            />
          </SubSection>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <EntityFormModal
        open={workerEdit.open}
        onOpenChange={(v) => { if (!v) workerEdit.closeModal(); }}
        title="Modifica lavoratore"
        fields={workerFields(companies || [], isClient, worker.companyId)}
        initialValues={workerEdit.editing || {}}
        onSubmit={workerEdit.save}
        submitLabel="Aggiorna"
      />
      <EntityFormModal
        open={fCrud.open}
        onOpenChange={(v) => { if (!v) fCrud.closeModal(); }}
        title={fCrud.editing?.id ? 'Modifica corso' : 'Nuovo corso formazione'}
        fields={formazioneFields([worker], companies || [], isClient, worker.companyId)}
        initialValues={fCrud.editing || {}}
        onSubmit={fCrud.save}
      />
      <EntityFormModal
        open={vCrud.open}
        onOpenChange={(v) => { if (!v) vCrud.closeModal(); }}
        title={vCrud.editing?.id ? 'Modifica visita medica' : 'Nuova visita medica'}
        fields={visitaFields([worker], companies || [], isClient, worker.companyId)}
        initialValues={vCrud.editing || {}}
        onSubmit={vCrud.save}
      />
      <EntityFormModal
        open={dCrud.open}
        onOpenChange={(v) => { if (!v) dCrud.closeModal(); }}
        title={dCrud.editing?.id ? 'Modifica DPI' : 'Nuova consegna DPI'}
        fields={dpiFields([worker], companies || [], isClient, worker.companyId)}
        initialValues={dCrud.editing || {}}
        onSubmit={dCrud.save}
      />

      <DismissWorkerDialog worker={worker} open={dismissOpen} onOpenChange={setDismissOpen} onDone={reloadWorker} />
      <RehireWorkerDialog worker={worker} open={rehireOpen} onOpenChange={setRehireOpen} onDone={() => { reloadWorker(); reloadF(); reloadV(); reloadD(); }} />
    </div>
  );
};

const Tag = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold border border-white/15">
    <Icon size={13} className="text-[#FFCB05]" /> {children}
  </span>
);
const Row = ({ label, children }) => (
  <div className="border-b border-slate-100 pb-2">
    <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</dt>
    <dd className="mt-0.5 text-sm text-slate-800">{children}</dd>
  </div>
);
const TabTrigger = ({ value, icon: Icon, children }) => (
  <TabsTrigger value={value} className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-2">
    <Icon size={14} /> {children}
  </TabsTrigger>
);
const SubSection = ({ title, onAdd, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
      <button onClick={onAdd} className="ro-hide px-3 py-1.5 rounded-full bg-[#fef7d4] text-[#8a6608] text-[11px] font-bold flex items-center gap-1.5 hover:bg-[#E6B800]"><Plus size={12} /> Aggiungi</button>
    </div>
    {children}
  </div>
);
const RelatedTable = ({ rows, cols, onEdit, onDelete }) => {
  if (!rows || rows.length === 0) return <div className="py-6 text-center text-sm text-slate-400">Nessun dato disponibile</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-gray-500">
            {cols.map((c, i) => (
              <th key={c.key} className={`text-left bg-gray-100 px-3 py-2 ${i === 0 ? 'rounded-l-xl' : ''}`}>{c.label}</th>
            ))}
            <th className="bg-gray-100 rounded-r-xl px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/60">
              {cols.map((c) => (
                <td key={c.key} className="px-3 py-2 text-gray-700">
                  {c.render ? c.render(r) : (c.fmt ? c.fmt(r[c.key]) : (r[c.key] || '—'))}
                </td>
              ))}
              <td className="px-3 py-2">
                <div className="flex gap-1">
                  <button onClick={() => onEdit(r)} className="p-1 rounded-md hover:bg-gray-100" title="Modifica"><Pencil size={13} className="text-gray-600" /></button>
                  <button onClick={() => onDelete(r)} className="ro-hide p-1 rounded-md hover:bg-gray-100" title="Elimina"><X size={14} className="text-red-600" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WorkerDetailPage;
